import { cloneDeep } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { LayerType, VectorType, type ILayerProps, type IVectorLayerProps } from '@/version-types';
import { pushHistory, requestVectorSVGPreview, updateLayer } from '../utils';

export interface IPieceColor {
    /**
     * 设置 graph 类型矢量图层片段颜色
     * @param layerID - 图层 id
     * @param index - 片段索引
     * @param color - 颜色
     * @returns
     */
    setPieceColor(
        layerID: string,
        index: number,
        color: string,
        options?: { writeHistory: boolean },
    ): ILayerProps | void;
}

export class PieceColor implements IPieceColor {
    private prevLayer: ILayerProps | null = null;

    constructor(private framework: IFramework) {}

    /**
     * 设置 Graph 素材的分块颜色。
     *
     * @param layerID - 目标图层 ID。
     * @param index - Graph 颜色分组索引。
     * @param color - 新颜色。
     * @param options - 是否提交历史；false 时使用 SVG preview。
     * @returns 更新后的图层；目标或索引无效时返回 undefined。
     */
    setPieceColor(layerID: string, index: number, color: string, options = { writeHistory: false }) {
        const prevLayer = (() => {
            if (this.prevLayer?.id === layerID) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { vectorType, colors: prevColors } = prevLayerData;

        if (vectorType !== VectorType.Graph || prevColors.length === 0) return;
        if (index < 0 || index >= prevColors.length) return;

        const { writeHistory } = options;

        if (writeHistory) {
            const nextLayer = this.framework.service.flatLayer.getLayer(layerID, true);

            requestVectorSVGPreview(this.framework, 'commit', nextLayer);
            updateLayer(this.framework, nextLayer);

            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.VECTOR_LAYER].colors);
            this.prevLayer = null;

            return nextLayer;
        }

        const nextColors = cloneDeep(prevColors);
        nextColors[index].color = color;

        const nextLayerData: IVectorLayerProps = { ...prevLayerData, colors: nextColors };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        requestVectorSVGPreview(this.framework, 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { dataOnly: true });

        return nextLayer;
    }
}
